import {AbstractStripePayment, type ConfirmResult, type TokenResult} from './abstract/class-abstract-stripe-payment';
import {createSetupIntent} from './setup-intent';
import {FormFields} from '../checkout';
import {StripeACHMandateElement} from './elements/class-stripe-achmandate-element';
class StripeAchdebitPayment extends AbstractStripePayment {
	public gatewayId() {
		return 'peachpay_stripe_achdebit';
	}

	public async tokenize(): Promise<TokenResult> {
		const clientSecret = await createSetupIntent('us_bank_account');
		if (!clientSecret) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to create setup intent.',
			}];
		}

		const {error: collectError, setupIntent: collectIntent} = await this.stripe.collectBankAccountForSetup({
			clientSecret,
			params: {
				payment_method_type: 'us_bank_account',
				payment_method_data: {
					billing_details: {
						name: FormFields.billing.firstName.get() + ' ' + FormFields.billing.lastName.get(),
					},
				},
			},
		});
		if (!collectIntent || collectError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: collectError?.message ?? 'Failed to collect bank account for setup intent.',
			}];
		}

		if (collectIntent.status !== 'requires_confirmation') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'The customer canceled choosing a bank account.',
			}];
		}

		const storeName = window.top?.location.hostname ?? '';
		let customerFullName;
		if (FormFields.billing.firstName.get()) {
			customerFullName = FormFields.billing.firstName.get() + ' ' + FormFields.billing.lastName.get();
		} else {
			customerFullName = null;
		}

		const mandateElement = new StripeACHMandateElement(storeName, customerFullName);
		let mandateResponse = false;
		try {
			mandateResponse = await mandateElement.confirm();
		} catch (error) {
			console.error(error);
		}

		if (!mandateResponse) {
			const message = 'The required mandate was not accepted. Payment canceled.';
			return [null, {note: message}];
		}

		const {error: confirmError, setupIntent: confirmIntent} = await this.stripe.confirmUsBankAccountSetup(clientSecret);
		if (!confirmIntent || confirmError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: confirmError?.message ?? 'Failed to confirm setup intent.',
			}];
		}

		if (confirmIntent.status !== 'succeeded') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
			}];
		}

		return [confirmIntent.payment_method as string, {}];
	}

	public async confirm(clientSecret: string): Promise<ConfirmResult> {
		const {paymentIntent, error} = await this.stripe.retrievePaymentIntent(clientSecret);

		if (!paymentIntent || error) {
			const message = error?.message ?? 'Failed to retrieve payment intent.';
			return [null, {
				note: message,
			}];
		}

		if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
			return ['success', {}];
		}

		return [null, {
			note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
		}];
	}
}

export {
	StripeAchdebitPayment,
};
